import * as THREE from 'three';
import { concatGeometry, mergeBoxes } from '../utils/geometry';
import { PALETTE } from '../assets/palette';

/**
 * Far/mid world layers framing the suspended highway: gradient sky dome with a
 * banded synthwave sun, star field, two skyline layers with lit windows, and a
 * scrolling grid floor deep in the void. Placement is deterministic (fixed
 * local seed) so screenshots stay reproducible across reloads.
 */
export class EnvironmentSystem {
  readonly group = new THREE.Group();

  private readonly disposables: Array<{ dispose(): void }> = [];
  private gridTexture: THREE.CanvasTexture | null = null;
  private readonly gridCellWorld: number;

  constructor() {
    this.buildSky();
    this.buildStars();
    this.gridCellWorld = this.buildVoidGrid();
    this.buildSkyline();
  }

  /** Scroll the void grid with travel so the world below reads as motion. */
  update(distanceDelta: number): void {
    if (this.gridTexture && distanceDelta !== 0) {
      const offset = this.gridTexture.offset;
      offset.y = (offset.y + distanceDelta / this.gridCellWorld) % 1;
    }
  }

  dispose(): void {
    for (const item of this.disposables) item.dispose();
  }

  // ---------- Sky ----------

  private buildSky(): void {
    const uniforms = {
      uTop: { value: new THREE.Color('#120a34') },
      uHorizon: { value: new THREE.Color('#2a0f4a') },
      uHorizonGlow: { value: new THREE.Color('#ff2e6d') },
      uSunTop: { value: new THREE.Color('#ffd94e') },
      uSunBottom: { value: new THREE.Color('#ff3d9e') },
      uSunDir: { value: new THREE.Vector3(0.05, 0.115, -1).normalize() },
    };
    const material = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms,
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        varying vec3 vDir;
        uniform vec3 uTop, uHorizon, uHorizonGlow, uSunTop, uSunBottom;
        uniform vec3 uSunDir;
        void main() {
          vec3 dir = normalize(vDir);
          float h = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);
          vec3 col = mix(uHorizon, uTop, pow(h, 0.55));
          // Magenta glow hugging the horizon line.
          col += uHorizonGlow * exp(-abs(dir.y) * 15.0) * 0.24;

          // Banded synthwave sun ahead of the track.
          vec3 sunDir = normalize(uSunDir);
          float d = dot(dir, sunDir);
          float disc = smoothstep(0.9982, 0.9990, d);
          float sunV = dir.y - sunDir.y;
          float bands = sunV < 0.0 ? step(0.42, fract(sunV * 95.0)) : 1.0;
          vec3 sunColor = mix(uSunBottom, uSunTop, clamp(sunV * 16.0 + 0.55, 0.0, 1.0));
          col += sunColor * disc * bands * 1.9;
          // Warm halo around the sun.
          col += uSunBottom * pow(max(d, 0.0), 30.0) * 0.3;

          gl_FragColor = vec4(col, 1.0);
        }`,
    });
    const geometry = new THREE.SphereGeometry(235, 32, 16);
    const sky = new THREE.Mesh(geometry, material);
    sky.frustumCulled = false;
    sky.renderOrder = -10;
    this.group.add(sky);
    this.disposables.push(geometry, material);
  }

  // ---------- Stars ----------

  private buildStars(): void {
    const rand = makeLcg(77);
    const count = 340;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      // Upper hemisphere shell, biased away from the horizon band.
      const azimuth = rand() * Math.PI * 2;
      const y = 0.12 + rand() * 0.85;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      positions[i * 3] = Math.cos(azimuth) * r * 220;
      positions[i * 3 + 1] = y * 220;
      positions[i * 3 + 2] = Math.sin(azimuth) * r * 220;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      color: '#cfe4ff',
      size: 1.4,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.8,
      depthWrite: false,
      fog: false,
    });
    const stars = new THREE.Points(geometry, material);
    stars.frustumCulled = false;
    this.group.add(stars);
    this.disposables.push(geometry, material);
  }

  // ---------- Void grid floor ----------

  private buildVoidGrid(): number {
    const size = 520;
    const repeats = 26;
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 128, 128);
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.85)';
    ctx.lineWidth = 3;
    ctx.strokeRect(1.5, 1.5, 125, 125);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(repeats, repeats);
    texture.anisotropy = 4;
    this.gridTexture = texture;

    const geometry = new THREE.PlaneGeometry(size, size);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0.38,
      depthWrite: false,
      color: '#7ad9ff',
    });
    const grid = new THREE.Mesh(geometry, material);
    grid.rotation.x = -Math.PI / 2;
    grid.position.y = -26;
    this.group.add(grid);
    this.disposables.push(geometry, material, texture);
    return size / repeats;
  }

  // ---------- Skyline ----------

  private buildSkyline(): void {
    const nearTexture = buildWindowTexture(PALETTE.skylineA, '#8fd8ff', 41);
    const farTexture = buildWindowTexture(PALETTE.skylineB, '#ff9ad4', 97);
    this.disposables.push(nearTexture, farTexture);

    // Three authored silhouettes: setback tower + antenna, wide slab with roof
    // units, and a stepped spire. Unit height 1 scales per instance.
    const tower = mergeBoxes([
      { geo: new THREE.BoxGeometry(1, 0.62, 1), pos: [0, 0.31, 0] },
      { geo: new THREE.BoxGeometry(0.74, 0.26, 0.74), pos: [0, 0.75, 0] },
      { geo: new THREE.BoxGeometry(0.48, 0.12, 0.48), pos: [0, 0.94, 0] },
      { geo: new THREE.BoxGeometry(0.05, 0.24, 0.05), pos: [0, 1.1, 0] },
    ]);
    const slab = mergeBoxes([
      { geo: new THREE.BoxGeometry(1.7, 0.8, 0.9), pos: [0, 0.4, 0] },
      { geo: new THREE.BoxGeometry(0.4, 0.14, 0.4), pos: [-0.45, 0.87, 0] },
      { geo: new THREE.BoxGeometry(0.26, 0.22, 0.26), pos: [0.4, 0.9, 0.1] },
    ]);
    const spire = concatGeometry(
      mergeBoxes([
        { geo: new THREE.BoxGeometry(0.9, 0.5, 0.9), pos: [0, 0.25, 0] },
        { geo: new THREE.BoxGeometry(0.66, 0.3, 0.66), pos: [0, 0.63, 0] },
        { geo: new THREE.BoxGeometry(0.42, 0.24, 0.42), pos: [0, 0.9, 0] },
      ]),
      (() => {
        const g = new THREE.ConeGeometry(0.16, 0.36, 4);
        g.translate(0, 1.2, 0);
        return g;
      })(),
    );
    const variants = [tower, slab, spire];
    for (const g of variants) this.disposables.push(g);

    const layers: Array<{
      texture: THREE.CanvasTexture;
      xMin: number;
      xMax: number;
      perVariant: number;
      heightMin: number;
      heightMax: number;
      seed: number;
    }> = [
      { texture: nearTexture, xMin: 17, xMax: 42, perVariant: 6, heightMin: 12, heightMax: 24, seed: 3 },
      { texture: farTexture, xMin: 34, xMax: 85, perVariant: 6, heightMin: 18, heightMax: 34, seed: 11 },
    ];

    const dummy = new THREE.Object3D();
    for (const layer of layers) {
      const material = new THREE.MeshBasicMaterial({ map: layer.texture, fog: false });
      this.disposables.push(material);
      const rand = makeLcg(layer.seed);
      for (const geometry of variants) {
        const mesh = new THREE.InstancedMesh(geometry, material, layer.perVariant * 2);
        let index = 0;
        for (const side of [-1, 1]) {
          for (let i = 0; i < layer.perVariant; i += 1) {
            const x = side * (layer.xMin + rand() * (layer.xMax - layer.xMin));
            const z = -30 - rand() * 190;
            const height = layer.heightMin + rand() * (layer.heightMax - layer.heightMin);
            const width = 4 + rand() * 6;
            dummy.position.set(x, -26, z);
            dummy.scale.set(width, height, width);
            dummy.rotation.set(0, rand() * Math.PI, 0);
            dummy.updateMatrix();
            mesh.setMatrixAt(index, dummy.matrix);
            index += 1;
          }
        }
        mesh.count = index;
        mesh.instanceMatrix.needsUpdate = true;
        mesh.frustumCulled = false;
        this.group.add(mesh);
        this.disposables.push(mesh);
      }
    }
  }
}

/** Tiny deterministic LCG so environment layout is identical on every load. */
function makeLcg(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0xffffffff;
  };
}

/** Dark facade with a sparse grid of lit windows; base at v=0 stays dark. */
function buildWindowTexture(wall: string, window: string, seed: number): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = wall;
  ctx.fillRect(0, 0, 64, 128);
  const rand = makeLcg(seed);
  ctx.fillStyle = window;
  for (let y = 10; y < 122; y += 8) {
    for (let x = 6; x < 58; x += 8) {
      if (rand() < 0.34) {
        ctx.globalAlpha = 0.55 + rand() * 0.45;
        ctx.fillRect(x, y, 4, 5);
      }
    }
  }
  ctx.globalAlpha = 1;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  return texture;
}
